import * as React from 'react'
import {
  Notepad,
  Phone,
  UsersThree,
  Storefront,
  Handshake,
  Alarm,
  HandCoins,
  EnvelopeOpen,
  FileText,
  AddressBook,
  Buildings,
  Tag,
  Truck,
  ShippingContainer,
  Megaphone,
  SealPercent,
  Package,
  LightningA,
  Sparkle,
  Plus,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { type BadgeColor } from './badge'
import { accentText, softRing } from './badge-colors'

/**
 * Activity icon library — the canonical map from a CRM activity *category* to its
 * icon, human label, and avatar tint. Single-sourced so every activity feed
 * (`ActivityTimeline`, the company/deal event history, the Action Center panel)
 * renders the same glyph and color for a given kind of event, instead of each
 * call site hand-picking an icon.
 *
 * The set and glyphs mirror the app's existing event history (client
 * `EventIcon.tsx`), re-pinned to `@phosphor-icons/react` and made **categorical**:
 * the icon names *what the event is about* (a deleted contact still shows the
 * contact icon), while the verb + tint carry the action. `automation` / `ai` are
 * actor-driven (First Bite Automation / AI recommendations) and win over the
 * category, matching how the app badges those rows.
 *
 * Pass an `ActivityItem.type` and the timeline resolves icon + color from here; a
 * per-item `icon` / `color` still overrides for one-offs.
 */

export type ActivityType =
  | 'note'
  | 'call'
  | 'meeting'
  | 'tradeShow'
  | 'deal'
  | 'reminder'
  | 'sample'
  | 'email'
  | 'document'
  | 'contact'
  | 'account'
  | 'tag'
  | 'distributor'
  | 'gpo'
  | 'campaign'
  | 'rebate'
  | 'product'
  | 'automation'
  | 'ai'
  | 'created'
  | 'updated'
  | 'deleted'

export interface ActivityTypeConfig {
  /** The category glyph (a `@phosphor-icons/react` icon). */
  icon: React.ReactNode
  /** Human label — for legends, filters, and `aria` text. */
  label: string
  /** Avatar tint (ring + icon) from the badge palette. */
  color: BadgeColor
}

/**
 * Category → { icon, label, color }. Colors are drawn from the badge palette so
 * activity avatars are distinguishable at a glance while staying on-system.
 */
export const activityTypeConfig: Record<ActivityType, ActivityTypeConfig> = {
  note: { icon: <Notepad />, label: 'Note', color: 'blue' },
  call: { icon: <Phone />, label: 'Call', color: 'teal' },
  meeting: { icon: <UsersThree />, label: 'Meeting', color: 'indigo' },
  tradeShow: { icon: <Storefront />, label: 'Trade show', color: 'orange' },
  deal: { icon: <Handshake />, label: 'Deal', color: 'primary' },
  reminder: { icon: <Alarm />, label: 'Reminder', color: 'amber' },
  sample: { icon: <HandCoins />, label: 'Sample', color: 'orange' },
  email: { icon: <EnvelopeOpen />, label: 'Email', color: 'sky' },
  document: { icon: <FileText />, label: 'Document', color: 'violet' },
  contact: { icon: <AddressBook />, label: 'Contact', color: 'teal' },
  account: { icon: <Buildings />, label: 'Account attribute', color: 'indigo' },
  tag: { icon: <Tag />, label: 'Tag', color: 'purple' },
  distributor: { icon: <Truck />, label: 'Distributor', color: 'cyan' },
  gpo: { icon: <ShippingContainer />, label: 'GPO', color: 'emerald' },
  campaign: { icon: <Megaphone />, label: 'Campaign', color: 'pink' },
  rebate: { icon: <SealPercent />, label: 'Rebate', color: 'lime' },
  product: { icon: <Package />, label: 'Product', color: 'yellow' },
  automation: { icon: <LightningA />, label: 'Automation', color: 'success' },
  ai: { icon: <Sparkle />, label: 'AI recommendation', color: 'fuchsia' },
  created: { icon: <Plus />, label: 'Created', color: 'success' },
  updated: { icon: <PencilSimple />, label: 'Updated', color: 'gray' },
  deleted: { icon: <Trash />, label: 'Deleted', color: 'rose' },
}

/** The uniform brand tone every activity icon uses unless tinted/overridden. */
const MONOCHROME_COLOR: BadgeColor = 'success'

/** Chip + glyph dimensions per size. */
const activityIconSize = {
  sm: 'size-7 [&>svg]:size-4',
  md: 'size-9 [&>svg]:size-[18px]',
} as const

export interface ActivityIconProps {
  /** Activity category — resolves the icon (and, when `tinted`, the color). */
  type?: ActivityType
  /** Icon override (wins over `type`'s icon). */
  icon?: React.ReactNode
  /** Explicit tint (wins over everything) — e.g. a destructive event in red. */
  color?: BadgeColor
  /**
   * Tint by the type's category color instead of the uniform brand tone.
   * Default false: the glyph carries the category, so color stays reserved for
   * meaning (state / destructive actions) rather than category identity.
   */
  tinted?: boolean
  /** Chip size — `md` (36px, default) or `sm` (28px, for denser feeds). */
  size?: 'sm' | 'md'
  className?: string
}

/**
 * The activity avatar chip — a white circle with a ring + glyph. Resolves its icon
 * from `type` (via {@link activityTypeConfig}); `icon` overrides it. **Monochrome
 * by default** (one brand tone) since the glyph already names the category — pass
 * `tinted` to use the type's category color, or `color` to force a specific tint
 * (e.g. red for a delete). Shared by `ActivityTimeline` and usable standalone.
 */
export const ActivityIcon = ({
  type,
  icon,
  color,
  tinted = false,
  size = 'md',
  className,
}: ActivityIconProps) => {
  const cfg = type ? activityTypeConfig[type] : undefined
  const resolvedIcon = icon ?? cfg?.icon
  const resolvedColor =
    color ?? (tinted ? cfg?.color : undefined) ?? MONOCHROME_COLOR
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-fbui-base-white ring-1',
        activityIconSize[size],
        softRing[resolvedColor],
        accentText[resolvedColor],
        className
      )}
    >
      {resolvedIcon}
    </span>
  )
}
ActivityIcon.displayName = 'ActivityIcon'

/** Ordered list of the category types (excludes the generic action fallbacks). */
export const ACTIVITY_TYPES = [
  'note',
  'call',
  'meeting',
  'tradeShow',
  'deal',
  'reminder',
  'sample',
  'email',
  'document',
  'contact',
  'account',
  'tag',
  'distributor',
  'gpo',
  'campaign',
  'rebate',
  'product',
] as const satisfies readonly ActivityType[]
